/**
 * Returns the registrable (shared) domain to use when setting or deleting the
 * `currentAccountId` cookie, matching the backend's ROOT_DOMAIN.
 *
 * The canonical "which Zapier environment is this" signal in this repo is
 * `process.env.NEXT_PUBLIC_ZAPIER_ORIGIN` (e.g. `https://zapier.com` or
 * `https://zapier-staging.com`).  We derive the shared cookie root from that
 * env var so the helper automatically follows any future origin changes without
 * requiring a separate string-literal update here.
 *
 * We still accept the live browser `hostname` as input (defaulting to
 * `window.location.hostname`) because cookie deletion/set decisions depend on
 * the actual host the browser is running on, not just the build-time origin.
 * We only return a shared domain when the browser host IS that origin host or
 * a subdomain of it; otherwise we fall back to host-only (undefined).
 *
 * Return values:
 * - `'.' + originHost`  when hostname matches the origin host or is a subdomain
 * - `undefined`         for localhost, unrecognised hosts, or when the env var
 *                       is unset / not a valid URL
 *
 * Not exported from index.ts — this is an internal helper.
 */
export function getCurrentAccountCookieDomain(
  hostname?: string
): string | undefined {
  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : undefined);

  if (!host) return undefined;

  // Normalize the incoming hostname: lowercase and strip a trailing FQDN dot.
  // Browsers normally supply lowercase hostnames without a trailing dot, but
  // callers (especially tests) may pass unnormalized strings.
  const normalizedHost = host.toLowerCase().replace(/\.$/, '');

  if (!normalizedHost || normalizedHost === 'localhost') return undefined;

  // Derive the origin host from the repo's canonical environment signal.
  // Guard against the env var being unset or not a parseable URL.
  const rawOrigin = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  if (!rawOrigin) return undefined;

  let originHost: string;
  try {
    originHost = new URL(rawOrigin).hostname;
  } catch {
    return undefined;
  }

  if (!originHost || originHost === 'localhost') return undefined;

  // Only return a shared domain when the browser is actually on the origin
  // host or a subdomain of it.  This prevents returning `.zapier.com` when
  // the app is accessed from an unrelated host (e.g. a raw Vercel preview).
  if (
    normalizedHost === originHost ||
    normalizedHost.endsWith('.' + originHost)
  ) {
    return '.' + originHost;
  }

  return undefined;
}
